import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { ArrowUpRight, Droplets, Building, ShieldCheck, Award, Landmark } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';

const Associations = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const associations = [
    {
      name: "AMPP/NACE",
      fullName: "Association for Materials Protection and Performance",
      description: "The leading global organization for corrosion control and protective coatings professionals.",
      website: "https://www.ampp.org",
      category: "Industry Standards"
    },
    {
      name: "AWWA",
      fullName: "American Water Works Association",
      description: "The largest nonprofit scientific and educational association dedicated to managing and treating water.",
      website: "https://www.awwa.org",
      category: "Water Industry"
    },
    {
      name: "WateReuse",
      fullName: "WateReuse Association",
      description: "The nation's only trade association solely dedicated to advancing laws, policy, funding, and public acceptance of water recycling.",
      website: "https://watereuse.org",
      category: "Water Industry"
    },
    {
      name: "APWA",
      fullName: "American Public Works Association",
      description: "The premier organization for public works professionals, advocating for infrastructure and community development.",
      website: "https://www.apwa.net",
      category: "Public Works"
    },
    {
      name: "IFMA",
      fullName: "International Facility Management Association",
      description: "The world's largest and most recognized association for facility management professionals.",
      website: "https://www.ifma.org",
      category: "Facility Management"
    },
    {
      name: "ISNetworld",
      fullName: "ISNetworld Contractor Network",
      description: "A global resource for connecting corporations with safe, reliable contractors and suppliers.",
      website: "https://www.isnetworld.com",
      category: "Compliance"
    },
    {
      name: "Avetta",
      fullName: "Avetta Supply Chain Risk",
      description: "A leading supply chain risk management platform connecting clients with qualified contractors.",
      website: "https://www.avetta.com",
      category: "Compliance"
    },
    {
      name: "WBENC",
      fullName: "Women's Business Enterprise National Council",
      description: "The largest certifier of women-owned businesses in the United States.",
      website: "https://www.wbenc.org",
      category: "Certification"
    },
    {
      name: "SBA WOSB",
      fullName: "SBA Women-Owned Small Business Program",
      description: "Federal contracting program that helps women-owned small businesses compete for federal contracts.",
      website: "https://www.sba.gov/federal-contracting/contracting-assistance-programs/women-owned-small-business-federal-contracting-program",
      category: "Certification"
    },
    {
      name: "CSLB",
      fullName: "Contractors State License Board",
      description: "California state agency that licenses and regulates the construction industry.",
      website: "https://www.cslb.ca.gov",
      category: "Regulatory"
    },
    {
      name: "DIR",
      fullName: "Department of Industrial Relations",
      description: "California agency that protects workers, improves working conditions, and advances opportunities for employment.",
      website: "https://www.dir.ca.gov",
      category: "Regulatory"
    }
  ];

  const getCategoryStyles = (category) => {
    switch(category) {
      case "Industry Standards": 
        return { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", icon: <ShieldCheck className="w-4 h-4" /> };
      case "Water Industry": 
        return { bg: "bg-cyan-50", border: "border-cyan-200", text: "text-cyan-700", icon: <Droplets className="w-4 h-4" /> };
      case "Public Works": 
        return { bg: "bg-slate-100", border: "border-slate-300", text: "text-slate-700", icon: <Landmark className="w-4 h-4" /> };
      case "Facility Management": 
        return { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: <Building className="w-4 h-4" /> };
      case "Compliance": 
        return { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: <ShieldCheck className="w-4 h-4" /> };
      case "Certification": 
        return { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", icon: <Award className="w-4 h-4" /> };
      case "Regulatory": 
        return { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", icon: <Landmark className="w-4 h-4" /> };
      default: 
        return { bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-700", icon: <Building className="w-4 h-4" /> };
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="associations-page">
      <Header />
      <SEO title={seoData["about/associations"]?.title} description={seoData["about/associations"]?.description} path="/about/associations" keywords={seoData["about/associations"]?.keywords || []} />
      
      <PageHero
        badge="Professional Affiliations"
        title="Industry Associations & Resources"
        description="Recognized organizations shaping standards and best practices across industrial coatings and infrastructure repair."
        pageKey="associations"
        showButton={false}
      />

      {/* Introduction */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-600 text-lg leading-relaxed">
            Our team participates within key industry associations and stays closely aligned with the 
            standards, specifications, and compliance frameworks developed by leading organizations 
            and certification bodies in the corrosion, coatings, and infrastructure sectors.
          </p>
        </div>
      </section>

      {/* Associations Grid */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {associations.map((association, index) => {
              const styles = getCategoryStyles(association.category);
              return (
                <a 
                  key={index} 
                  href={association.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <div className={`h-full p-6 rounded-xl border-2 ${styles.border} ${styles.bg} 
                    hover:shadow-lg hover:shadow-blue-100 hover:border-blue-400 
                    transition-all duration-300 transform hover:-translate-y-1`}>
                    
                    {/* Category badge */}
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold mb-4 ${styles.bg} ${styles.text} border ${styles.border}`}>
                      {styles.icon}
                      {association.category}
                    </div>
                    
                    {/* Content */}
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="text-xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                        {association.name}
                      </h3>
                      <ArrowUpRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 shrink-0 mt-0.5" />
                    </div>
                    
                    <p className="text-sm font-medium text-slate-500 mb-3">
                      {association.fullName}
                    </p>
                    
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {association.description}
                    </p>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Associations;
